import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { isPaymentGuaranteeType } from '@features/booking/state/types';
import { resolveVenueCountry } from '@features/booking/utils/billingCountry';
import { documentTypesFor } from '@features/booking/utils/documentTypes';
import {
  receiptLabelKeys,
  LEGACY_OVERRIDE_KEYS,
  type ReceiptLabelKeys,
} from '@features/booking/utils/billingReceiptLabels';
import type { i18n as I18nInstance } from 'i18next';

const PERU = 'PE';
const ARGENTINA = 'AR';

export interface BillingReceiptInput {
  countryCode?: string | null;
  /** The admin's ISO code, used only when the dial code names no country. */
  isoCountryCode?: string | null;
  allowBillingReceiptType: boolean;
  allowFullBillingDetails: boolean;
  guaranteeType: string | null | undefined;
  /** The venue's processor: a MercadoPago venue with the billing flag collects the document too. */
  paymentProcessor?: string | null;
}

export interface BillingReceiptDecision {
  /** Render the receipt selector and its fiscal fields. */
  showReceiptSelector: boolean;
  /** Render the generic billing address block. */
  showBillingAddress: boolean;
  /** Render the identity document field inside the generic block. */
  showDocumentField: boolean;
  /**
   * Whether a blank document blocks the payment. Argentina's field has always been optional
   * (blank pays with the random `Otro` document) and stays so; everywhere else the field is
   * collected to be measured (BD-2), so it is required. A venue whose country does not resolve
   * is a data problem, never a wall: its field stays optional.
   */
  documentRequired: boolean;
  /** The document types the field offers, the country's usual one first (documentTypes.ts). */
  documentTypes: readonly string[];
  country: string | null;
  /** i18n keys for each label, country-specific first. */
  labelKeys: ReceiptLabelKeys;
}

/** Each label resolved to the text the guest reads. */
export type ResolvedReceiptLabels = Record<keyof ReceiptLabelKeys, string>;

/**
 * Decides which billing blocks belong on the payment step.
 *
 * Peru keeps an exemption: it saw the fiscal block before the flag existed and
 * must keep seeing it whether or not the flag is on. Everyone else opts in
 * through allowBillingReceiptType. Country only ever picks the wording — visibility
 * is the flag's job.
 *
 * The two blocks are independent, each on its own flag, exactly as they are in
 * the Angular widget: a Peruvian venue with allowFullBillingDetails has always
 * rendered the generic address alongside the fiscal block there.
 *
 * They do overlap — the fiscal block collects an address of its own — and the
 * duplicate field was briefly removed on the grounds that asking twice costs
 * conversion. That took `billingAddress` with it: the two addresses are not the
 * same datum. `billingAddress` is a column on the reservation, while the fiscal
 * one is flattened into billing_notes, so suppressing the generic block left
 * that column empty for every venue running both flags.
 */
export function decideBillingReceipt(input: BillingReceiptInput): BillingReceiptDecision {
  const country = resolveVenueCountry(input.isoCountryCode, input.countryCode);
  const hasGuarantee = isPaymentGuaranteeType(input.guaranteeType);

  const showReceiptSelector = hasGuarantee && (country === PERU || input.allowBillingReceiptType);
  const showBillingAddress = hasGuarantee && input.allowFullBillingDetails;
  const showDocumentField = hasGuarantee && input.allowFullBillingDetails
    && (country === ARGENTINA || input.paymentProcessor === 'mercadopago');

  return {
    showReceiptSelector,
    showBillingAddress,
    // The bare identity document is not the fiscal block's document repeated:
    // `identityDocument` is what MercadoPago charges against (the payer's
    // `identification`), while billPersonalDocument is what the venue invoices
    // against, and the two can belong to different people. Argentina has always
    // been asked for it; a venue elsewhere that collects through MercadoPago
    // gains it with the same flag (A5 of
    // docs/plans/2026-10-21-mercadopago-approval-hardening.md), so a real
    // document reaches MercadoPago's antifraud instead of a random one. The
    // flag stays the switch: nothing shows at a venue that never asked.
    showDocumentField,
    documentRequired: showDocumentField && country !== null && country !== ARGENTINA,
    documentTypes: documentTypesFor(country),
    country,
    labelKeys: receiptLabelKeys(country),
  };
}

/**
 * Drops the bare legacy-override key from each fallback list unless the
 * CURRENT language actually carries an injected value for it (a pinned
 * lookup — no language fallback). The bare key has no static per-language
 * default to shield it, so left in place it would resolve through the full
 * language hierarchy and leak a venue's single-language customization into
 * every other language — unlike every other overridable key (PR #390
 * review). Exported so the resolution tests exercise the same gate the hook
 * applies.
 */
export function resolveReceiptLabelKeys(
  i18nInstance: I18nInstance,
  // Deliberately wider than the call site's type: `resolvedLanguage` and
  // `language` are both typed `string` by i18next but are undefined until init
  // resolves, which is exactly the case the gate below guards.
  lang: string | undefined,
  labelKeys: ReceiptLabelKeys,
): ReceiptLabelKeys {
  // `getResource` reads the store for ONE language — no resolution hierarchy
  // involved, so this cannot be re-broken by an upstream change to fallback
  // handling. (i18next 25.7.3's per-call `fallbackLng: false` does work —
  // `toResolveHierarchy` special-cases `fallbackCode === false` — but the
  // store read doesn't depend on that staying true.) Matching against
  // LEGACY_OVERRIDE_KEYS instead of assuming list position keeps a reorder
  // in receiptLabelKeys from making this slice off a country key.
  const gate = (keys: string[]) => {
    if (!LEGACY_OVERRIDE_KEYS.has(keys[0])) return keys;
    // No language resolved yet — i18next's getResource throws on an undefined
    // one rather than returning undefined, which took the whole payment step
    // down with it. An unresolved language cannot carry an injected override,
    // so it reads as "no value" and the legacy key drops, keeping the
    // cross-language leak shut either way.
    if (!lang) return keys.slice(1);
    return i18nInstance.getResource(lang, 'booking', keys[0]) !== undefined
      ? keys
      : keys.slice(1);
  };

  return {
    personalReceipt: gate(labelKeys.personalReceipt),
    businessReceipt: gate(labelKeys.businessReceipt),
    personalDocument: gate(labelKeys.personalDocument),
    businessDocument: gate(labelKeys.businessDocument),
  };
}

export function useBillingReceipt(): BillingReceiptDecision & { labels: ResolvedReceiptLabels } {
  const { t, i18n } = useTranslation('booking');
  const booking = useBooking();
  const restaurant = useRestaurant();

  const { countryCode, isoCountryCode, allowBillingReceiptType, allowFullBillingDetails, paymentProcessor } = restaurant;
  const { guaranteeType } = booking;

  // BillingSection re-renders on every keystroke in a billing field, so the
  // decision is memoised rather than recomputed per character.
  const decision = useMemo(
    () =>
      decideBillingReceipt({
        countryCode,
        isoCountryCode,
        allowBillingReceiptType: Boolean(allowBillingReceiptType),
        allowFullBillingDetails: Boolean(allowFullBillingDetails),
        guaranteeType,
        paymentProcessor,
      }),
    [countryCode, isoCountryCode, allowBillingReceiptType, allowFullBillingDetails, guaranteeType, paymentProcessor],
  );

  // Resolved outside the memo so the labels follow `t` rather than the
  // decision's inputs. `t` is refreshed on the events react-i18next is bound
  // to — a language change, and the store's 'added' event that carries a
  // venue's injected appTexts, which config.ts opts into for exactly this.
  const { labelKeys } = decision;

  // Gated per render on purpose: both the language and the injected bundles
  // can change after mount (the 'added' event config.ts binds to), and the
  // gate must follow them just like `t` does.
  const gated = resolveReceiptLabelKeys(
    i18n,
    i18n.resolvedLanguage ?? i18n.language,
    labelKeys,
  );

  return {
    ...decision,
    labels: {
      personalReceipt: t(gated.personalReceipt),
      businessReceipt: t(gated.businessReceipt),
      personalDocument: t(gated.personalDocument),
      businessDocument: t(gated.businessDocument),
    },
  };
}
